import * as React from 'react';

import { RaiselyDonateEmbed } from '@/components/layout/raisely-donate';

const donatePage = {
  eyebrow: 'Donate',
  title: 'Support NextOnVera',
  lead: 'Every gift, at every level, pools into the same Brain Cancer Fund, backing the NextOnVera validation program and early-stage brain cancer research that traditional funding will not touch yet. Your donation is received by Tour de Cure, which issues your tax-deductible receipt.'
};

export function DonatePage() {
  React.useEffect(() => {
    const previous = document.title;
    document.title = 'Support NextOnVera | NextOnVera';
    return () => {
      document.title = previous;
    };
  }, []);

  return (
    <section className="relative isolate overflow-hidden">
      {/* Site-themed backdrop image at 70% transparency, layered over the warm paper base */}
      <div aria-hidden="true" className="bg-background absolute inset-0 -z-20" />
      <img
        src="/generated-images/donate-page-backdrop.png"
        alt=""
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 -z-10 h-full w-full object-cover object-center opacity-30"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 -z-10 bg-gradient-to-b from-background/60 via-transparent to-background/70"
      />

      <article className="px-4 py-14 sm:px-6 sm:py-16 lg:px-8">
        <div className="mx-auto w-full max-w-6xl">
          <p className="text-primary mb-3 text-xs font-medium tracking-[0.2em] uppercase">
            {donatePage.eyebrow}
          </p>
          <h1 className="font-display text-foreground text-4xl font-semibold tracking-tight sm:text-5xl">
            {donatePage.title}
          </h1>
          <p className="text-muted-foreground mt-5 max-w-2xl text-base leading-relaxed sm:text-lg">
            {donatePage.lead}
          </p>

          {/* Donation window (70% width, left) + donation notes panel (right) */}
          <div className="mt-10 grid grid-cols-1 items-start gap-8 lg:grid-cols-10">
            <div className="lg:col-span-7">
              <div className="border-primary/40 relative overflow-hidden rounded-2xl border-2 bg-white shadow-[0_24px_60px_-40px_rgb(10_122_147_/_0.45)]">
              {/* NextOnVera header strip */}
              <div className="border-primary/30 flex items-center justify-between gap-4 border-b bg-gradient-to-r from-[#0A7A93]/10 via-transparent to-[#0A7A93]/10 px-5 py-3.5 sm:px-6">
                <div className="flex items-center gap-2.5">
                  <span className="relative flex h-3 w-3">
                    <span className="bg-primary/30 absolute inline-flex h-full w-full rounded-full" />
                    <span className="bg-primary relative inline-flex h-3 w-3 rounded-full" />
                  </span>
                  <p className="font-display text-primary text-sm font-semibold tracking-[0.18em] uppercase">
                    NextOnVera
                  </p>
                </div>
                <p className="text-muted-foreground hidden text-xs tracking-wide sm:block">
                  Donations power the NextOnVera platform and early-stage research
                </p>
              </div>

              <RaiselyDonateEmbed
                height={800}
                className="-mx-5 -my-3 w-[calc(100%+2.5rem)] [&_iframe]:block"
              />

              {/* Donation processing and tax-deductibility note */}
              <p className="text-muted-foreground border-t border-border/60 px-5 py-4 text-xs leading-relaxed sm:px-6">
                Donations are processed securely by Raisely on behalf of The
                Trustee for Tour de Cure Trust (ABN 56 302 713 357), a
                registered Australian charity with deductible gift recipient
                status. Donations of $2 or more are tax deductible in Australia
                where eligible. Receipts are issued by Tour de Cure.
              </p>
              </div>
            </div>
            {/* Donation notes — where the money goes */}
            <aside className="border-primary/30 rounded-2xl border bg-white/80 p-6 shadow-[0_16px_40px_-30px_rgb(10_122_147_/_0.4)] sm:p-7 lg:col-span-3">
              <h2 className="font-display text-foreground text-lg font-semibold tracking-tight sm:text-xl">
                Where your donation goes
              </h2>
              <p className="text-muted-foreground mt-3 text-sm leading-relaxed">
                Every gift pools into the Brain Cancer Fund, governed by Tour
                de Cure’s twenty-year grants discipline.
              </p>
              <ul className="mt-5 space-y-4">
                {[
                  {
                    title: 'NextOnVera validation',
                    body: 'Funds the studies, clinical governance and regulatory work needed to find out whether NextOnVera is safe and useful for oncology teams, and to make it available to them if it is, subject to regulatory approval.'
                  },
                  {
                    title: 'Early-stage research',
                    body: 'Backs high-risk, high-potential brain cancer ideas too early for NHMRC or MRFF, but too important to lose.'
                  },
                  {
                    title: 'Multi-year certainty',
                    body: 'Multi-year gifts give the research grants pool the certainty to plan bold, longer-horizon work.'
                  }
                ].map((item) => (
                  <li key={item.title} className="flex gap-3">
                    <span aria-hidden="true" className="bg-primary mt-1.5 h-2 w-2 shrink-0 rounded-full" />
                    <div>
                      <p className="text-foreground text-sm font-semibold">{item.title}</p>
                      <p className="text-muted-foreground mt-1 text-sm leading-relaxed">{item.body}</p>
                    </div>
                  </li>
                ))}
              </ul>
              <p className="text-muted-foreground mt-6 border-t border-border/60 pt-4 text-xs leading-relaxed">
                An independent, competitive process directs every dollar — with
                full transparency on what is funded and what is found.
              </p>
            </aside>
          </div>
        </div>
      </article>
    </section>
  );
}
